import { useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useFocusShortcut } from '@/hooks/useFocusShortcut';
import { cn } from '@/lib/utils';
import { FIELD_CLEAR_BUTTON_CLASS, FIELD_CONTROL_CLASS } from '@/styles/fieldControl';

const TOOLBAR_WIDTH_CLASS = 'flex-1 min-w-48 max-sm:basis-full lg:w-60 lg:flex-none';
const FULL_WIDTH_CLASS = 'w-full';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isFullWidth?: boolean | undefined;
  disabledReason?: string | undefined;
  className?: string;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  isFullWidth = false,
  disabledReason,
  className,
}: SearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  useFocusShortcut(inputRef);
  const isDisabled = disabledReason !== undefined;
  const hasClearButton = value !== '' && !isDisabled;

  function handleClear() {
    onChange('');
    inputRef.current?.focus();
  }

  return (
    <div
      className={cn('relative', isFullWidth ? FULL_WIDTH_CLASS : TOOLBAR_WIDTH_CLASS, className)}
      title={disabledReason}
    >
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-brand-link/70" />
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isDisabled}
        className={cn(FIELD_CONTROL_CLASS, 'h-9 w-full pl-9', hasClearButton && 'pr-9')}
      />
      {hasClearButton ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Clear search"
          onClick={handleClear}
          className={FIELD_CLEAR_BUTTON_CLASS}
        >
          <X className="size-3" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
